<template>
  <audio
    ref="audio"
    :src="src"
    loop
    preload="auto"
    playsinline
    @canplaythrough="tryStart"
  />
</template>

<script>
export default {
  name: "BackgroundMusic",
  props: {
    playing: {
      type: Boolean,
      default: true,
    },
    volume: {
      type: Number,
      default: 0.25,
    },
    src: {
      type: String,
      default: "/audio/background.mp3",
    },
  },
  data() {
    return {
      started: false,
      interactionHandlers: [],
    };
  },
  watch: {
    playing(isPlaying) {
      if (isPlaying) {
        this.started = false;
        this.tryStart();
      } else {
        this.stop();
      }
    },
    volume(value) {
      const audio = this.$refs.audio;
      if (audio && !audio.muted) {
        audio.volume = value;
      }
    },
  },
  mounted() {
    this.tryStart();
    this.bindInteractionUnlock();
  },
  beforeDestroy() {
    this.clearInteractionHandlers();
  },
  methods: {
    getAudio() {
      return this.$refs.audio;
    },
    tryStart() {
      if (!this.playing || this.started) return;

      const audio = this.getAudio();
      if (!audio) return;

      audio.volume = this.volume;
      audio.muted = false;

      const playPromise = audio.play();
      if (!playPromise || typeof playPromise.then !== "function") {
        this.started = !audio.paused;
        return;
      }

      playPromise
        .then(() => {
          this.started = true;
          this.clearInteractionHandlers();
        })
        .catch(() => {
          // Browser blocks audible autoplay — start muted, unmute on first interaction.
          audio.muted = true;
          audio.play().then(() => {
            this.started = true;
          }).catch(() => {});
        });
    },
    stop() {
      const audio = this.getAudio();
      if (audio) {
        audio.pause();
      }
      this.started = false;
    },
    unlockAudio() {
      const audio = this.getAudio();
      if (!audio || !this.playing) return;

      audio.muted = false;
      audio.volume = this.volume;

      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => {
            this.started = true;
            this.clearInteractionHandlers();
          })
          .catch(() => {});
      }
    },
    bindInteractionUnlock() {
      const events = ["pointerdown", "click", "touchstart", "keydown", "wheel"];

      events.forEach((eventName) => {
        const handler = () => this.unlockAudio();
        this.interactionHandlers.push({ eventName, handler });
        window.addEventListener(eventName, handler, { capture: true, passive: true });
      });
    },
    clearInteractionHandlers() {
      this.interactionHandlers.forEach(({ eventName, handler }) => {
        window.removeEventListener(eventName, handler, { capture: true });
      });
      this.interactionHandlers = [];
    },
  },
};
</script>
